import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Coins, Loader2, ArrowRight, ChevronDown, Lock, Phone, MessageCircle, Headset } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LowBalanceBanner } from "@/components/LowBalanceBanner";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import MicButton from "@/components/MicButton";
import { trackEvent } from "@/lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PERSONA_KEYS = ["advisor", "academician", "strategist"];
const PREMIUM_PERSONAS = ["academician", "strategist"];
const PREMIUM_MIN_TOKENS = 15;
const HOTLINE = "+40730112810";
const WA = (n) => `https://wa.me/${n.replace(/[^0-9]/g, "")}`;

/**
 * OXO conversational advisor — localized persona (Roxana/Anna/Emily) with an
 * operational-mode selector (empathetic advisor / academician / shipping strategist).
 * variant="full" | "sidebar" | "hero"
 */
export default function EmmyAdvisor({ variant = "full", quickPrompts = false }) {
  const compact = variant === "sidebar";
  const hero = variant === "hero";
  const { user, setTokens } = useAuth();
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sid, setSid] = useState("");
  const [loading, setLoading] = useState(false);
  const [awaiting, setAwaiting] = useState(false);
  const [balance, setBalance] = useState(user?.tokens_balance ?? 0);
  const [persona, setPersona] = useState("advisor");
  const [menuOpen, setMenuOpen] = useState(false);
  const [expertOpen, setExpertOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const endRef = useRef(null);
  const navigate = useNavigate();

  const isPremiumUser = !!user && balance >= PREMIUM_MIN_TOKENS;
  const choosePersona = (k) => {
    setMenuOpen(false);
    if (PREMIUM_PERSONAS.includes(k) && !isPremiumUser) { setGateOpen(true); return; }
    setPersona(k);
  };

  const lang = i18n.language || "ro";
  const name = t("ux.emmy.name");
  const avatar = t("ux.emmy.avatar");
  const pMeta = (k) => ({
    label: t(`ux.emmy.personas.${k}.name`),
    sub: t(`ux.emmy.personas.${k}.sub`),
    ph: t(`ux.emmy.personas.${k}.ph`),
  });
  const current = pMeta(persona);
  const STARTERS = [t("ux.emmy.starter1"), t("ux.emmy.starter2"), t("ux.emmy.starter3")];

  useEffect(() => {
    if (!user) return;
    api.get("/tokens/balance").then((r) => setBalance(r.data.tokens_balance)).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (messages.length || loading) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  const send = async (text, confirmDeep = false) => {
    if (!user) { toast.info(t("ux.emmy.loginHint")); return; }
    const content = (text ?? input).trim();
    if (!content && !confirmDeep) return;
    if (!confirmDeep) setMessages((m) => [...m, { role: "user", content }]);
    setInput("");
    setLoading(true);
    setAwaiting(false);
    try {
      const r = await api.post("/advisor/chat", {
        message: confirmDeep ? "Da, continuăm." : content,
        session_id: sid, lang, persona, confirm_deep: confirmDeep,
      });
      const d = r.data;
      setSid(d.session_id);
      setBalance(d.tokens_balance);
      setTokens(d.tokens_balance);
      setAwaiting(d.awaiting_payment);
      setMessages((m) => [...m, { role: "assistant", content: d.reply, charged: d.tokens_charged }]);
      if (d.tokens_charged > 0) trackEvent("deep_analysis", { value: d.tokens_charged, currency: "OXO" });
    } catch (e) {
      if (e?.response?.status === 402) toast.error(t("ux.emmy.insufficient"));
      else toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className={`flex flex-col ${compact ? "h-full border-l-2 border-amber-500/50 bg-slate-50 pl-2" : ""} ${hero ? "border-l-2 border-amber-500/50 bg-slate-50" : ""}`} data-testid={compact ? "emmy-sidebar" : (hero ? "emmy-hero" : "advisor-page")}>
      {/* High-value accent badge */}
      <div className="bg-amber-500 text-slate-900 text-[9px] font-black uppercase tracking-[0.12em] px-3 py-1 flex items-center gap-1.5" data-testid="emmy-badge">
        <Sparkles className="w-3 h-3" /> {hero ? t("ux.emmy.heroBadge") : t("ux.emmy.badge")}
      </div>

      {/* Header — localized persona avatar + name + mode selector */}
      <div className="flex items-center justify-between gap-2 border border-slate-900 bg-slate-900 text-white px-3 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src={avatar} alt={name} className="w-9 h-9 shrink-0 object-cover border border-amber-500/60" data-testid="emmy-avatar" />
          <div className="min-w-0">
            <div className="font-black tracking-tight leading-tight truncate" data-testid="emmy-name">{name}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-orange-400 truncate" data-testid="emmy-persona-sub">{t("ux.emmy.aiLabel")}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Live human expert handoff */}
          <button onClick={() => setExpertOpen(true)} className="inline-flex items-center gap-1.5 border border-green-500/50 bg-green-500/10 hover:bg-green-500/20 text-green-300 px-2 py-1 text-[11px] font-bold transition-colors" data-testid="live-expert-btn">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" /></span>
            <span className="hidden sm:inline">{t("ux.emmy.liveExpert")}</span>
          </button>
          {/* Micro persona/tier selector */}
          <div className="relative">
            <button onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-1 border border-white/20 hover:border-amber-500 px-2 py-1 text-[11px] font-semibold transition-colors max-w-[120px]" data-testid="persona-select">
              <span className="truncate">{current.label}</span> <ChevronDown className="w-3 h-3 shrink-0" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 w-60 bg-white text-slate-800 border border-slate-200 shadow-xl" data-testid="persona-menu">
                {PERSONA_KEYS.map((k) => {
                  const meta = pMeta(k);
                  const locked = PREMIUM_PERSONAS.includes(k) && !isPremiumUser;
                  return (
                    <button key={k} onClick={() => choosePersona(k)}
                      className={`w-full text-left px-3 py-2 hover:bg-orange-50 transition-colors flex items-start justify-between gap-2 ${persona === k ? "bg-orange-50" : ""}`} data-testid={`persona-opt-${k}`}>
                      <span className="min-w-0">
                        <span className="text-[13px] font-bold text-slate-900 block">{meta.label}</span>
                        <span className="text-[11px] text-slate-500 leading-snug block">{meta.sub}</span>
                      </span>
                      {locked && <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wide text-amber-600 bg-amber-100 px-1.5 py-0.5 shrink-0 mt-0.5" data-testid={`persona-lock-${k}`}><Lock className="w-2.5 h-2.5" /> {t("ux.emmy.gate.locked")}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <span className="inline-flex items-center gap-1 border border-white/20 px-2 py-1 text-xs font-bold" data-testid="advisor-balance">
            <Coins className="w-3.5 h-3.5 text-amber-400" /> {balance}
          </span>
        </div>
      </div>

      {balance < 15 && user && <div className="mt-2"><LowBalanceBanner balance={balance} /></div>}

      {/* Messages */}
      <div className={`border-x border-b border-slate-200 bg-white flex flex-col flex-1 ${compact ? "min-h-0" : (hero ? "h-[42vh]" : "h-[58vh]")}`}>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="advisor-messages">
          {messages.length === 0 && (
            <div className="text-slate-500 py-2">
              <p className="text-sm font-semibold text-slate-700 mb-1" data-testid="emmy-greeting">{t("ux.emmy.hi", { name })}</p>
              <p className="text-xs text-slate-500 mb-3">{t("ux.emmy.greetSub")}</p>
              <div className="flex flex-col gap-2">
                {STARTERS.slice(0, compact ? 2 : 3).map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="text-left text-[13px] leading-snug border border-slate-200 hover:border-orange-500 hover:bg-orange-50/50 px-3 py-2 transition-colors" data-testid={`advisor-starter-${i}`}>{s}</button>
                ))}
              </div>
              {!user && (
                <div className="mt-4 pt-3 border-t border-dashed border-slate-200" data-testid="emmy-save-prompt">
                  <p className="text-[11px] text-slate-500 mb-2 leading-snug">{t("ux.emmy.saveHint")}</p>
                  <GoogleSignInButton compact label={t("ux.emmy.saveCta")} />
                </div>
              )}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`advisor-msg-${m.role}`}>
              <div className={`max-w-[88%] px-3.5 py-2.5 text-[13px] whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800"}`}>
                {m.content}
                {m.charged > 0 && <div className="text-[10px] uppercase tracking-wide text-amber-600 mt-2 font-bold">− {m.charged} · {t("ux.emmy.deepDone")}</div>}
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="bg-slate-100 px-4 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div></div>}
          {awaiting && !loading && (
            <div className="flex justify-start">
              <button onClick={() => send(null, true)} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 px-3.5 py-2.5 text-[13px] font-bold transition-colors" data-testid="advisor-confirm-deep">
                <ArrowRight className="w-4 h-4" /> {t("ux.emmy.deepCta")}
              </button>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-200 p-2.5 flex gap-2 items-center">
          <div className="relative flex-1">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !loading && send()}
              placeholder={current.ph} className="w-full pl-3 pr-11 py-2.5 border border-slate-300 text-sm focus:outline-none focus:border-orange-500" data-testid="advisor-input" />
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <MicButton testid="advisor-mic" size="w-4 h-4"
                onInterim={(txt) => setInput(txt)}
                onResult={(txt) => setInput(txt)} />
            </div>
          </div>
          <button onClick={() => send()} disabled={loading || !input.trim()} className="bg-orange-600 hover:bg-orange-700 text-white px-3.5 transition-colors disabled:opacity-50" data-testid="advisor-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
        {quickPrompts && (
          <div className="border-t border-slate-100 bg-slate-50 p-2 flex flex-wrap gap-1.5" data-testid="emmy-quick-prompts">
            {[t("ux.emmy.quick1"), t("ux.emmy.quick2"), t("ux.emmy.quick3")].map((q, i) => (
              <button key={i} onClick={() => send(q)} className="text-left text-[11px] leading-tight border border-slate-300 hover:border-orange-500 hover:bg-white bg-white/70 text-slate-700 px-2 py-1.5 transition-colors flex-1 min-w-[160px]" data-testid={`emmy-quick-${i}`}>
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-2 px-0.5">{t("ux.emmy.footnote")}</p>

      {/* Human expert hotline modal */}
      <Dialog open={expertOpen} onOpenChange={setExpertOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="expert-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-black">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>
              {t("ux.emmy.expert.title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed">{t("ux.emmy.expert.sub")}</p>
          <div className="space-y-2.5 mt-1">
            {[{ role: t("ux.emmy.expert.woodTech"), name: "Ing. József Nagy" }, { role: t("ux.emmy.expert.shipping"), name: "Radu Marinescu" }].map((e, i) => (
              <div key={i} className="border border-slate-200 p-3 flex items-center justify-between gap-2" data-testid={`expert-row-${i}`}>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-orange-600">{e.role}</div>
                  <div className="font-bold text-slate-900 text-sm truncate">{e.name}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a href={WA(HOTLINE)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-2.5 py-2 text-xs font-bold transition-colors" data-testid={`expert-wa-${i}`}><MessageCircle className="w-3.5 h-3.5" /> {t("ux.emmy.expert.wa")}</a>
                  <a href={`tel:${HOTLINE}`} className="inline-flex items-center gap-1 border border-slate-300 hover:border-slate-900 text-slate-800 px-2.5 py-2 text-xs font-bold transition-colors" data-testid={`expert-call-${i}`}><Phone className="w-3.5 h-3.5" /> {t("ux.emmy.expert.call")}</a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5"><Headset className="w-3.5 h-3.5" /> {t("ux.emmy.expert.note")}</p>
        </DialogContent>
      </Dialog>

      {/* Premium persona gate */}
      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent className="max-w-sm rounded-none" data-testid="persona-gate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-black"><Lock className="w-5 h-5 text-amber-500" /> {t("ux.emmy.gate.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed">{t("ux.emmy.gate.sub")}</p>
          <div className="flex flex-col gap-2 mt-1">
            <button onClick={() => { setGateOpen(false); navigate(user ? "/tokens" : "/autentificare"); }} className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="gate-cta">
              <Sparkles className="w-4 h-4" /> {t("ux.emmy.gate.cta")}
            </button>
            <button onClick={() => { setGateOpen(false); navigate("/preturi"); }} className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-orange-600 hover:text-orange-600 px-4 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="gate-pricing-cta">
              {t("ux.emmy.gate.plans")}
            </button>
            <button onClick={() => setGateOpen(false)} className="text-sm text-slate-500 hover:text-slate-800 py-1" data-testid="gate-later">{t("ux.emmy.gate.later")}</button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">{t("ux.emmy.gate.note")}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
